export interface BlogPostMetadata {
  title: string;
  slug: string;
  author: string;
  date: string;
  description: string;
  image?: string;
  lang: string;
}

export interface BlogPost extends BlogPostMetadata {
  content: string;
}

function parseFrontmatter(raw: string): { data: Record<string, string>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) {
    return { data: {}, content: raw };
  }

  const data: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) {
      continue;
    }
    const key = line.slice(0, colonIdx).trim();
    const value = line.slice(colonIdx + 1).trim();
    data[key] = value;
  }

  return { data, content: match[2] };
}

// @ts-expect-error Vite-specific import.meta.glob API, not recognized by CommonJS module setting
const modules = import.meta.glob('./posts/*.md', { query: '?raw', import: 'default' }) as Record<
  string,
  () => Promise<string>
>;

let cachedPosts: BlogPost[] | null = null;

async function loadPosts(): Promise<BlogPost[]> {
  if (cachedPosts) {
    return cachedPosts;
  }

  const posts: BlogPost[] = [];

  for (const [, resolver] of Object.entries(modules)) {
    const content = await resolver();
    const { data, content: mdContent } = parseFrontmatter(content);

    if (data.slug) {
      posts.push({
        title: data.title || 'Untitled',
        slug: data.slug,
        author: data.author || 'Anonymous',
        date: data.date || new Date().toISOString(),
        description: data.description || '',
        image: data.image || undefined,
        lang: data.lang || 'en',
        content: mdContent,
      });
    }
  }

  posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  cachedPosts = posts;
  return posts;
}

export async function getAllPosts(lang: string): Promise<BlogPostMetadata[]> {
  const posts = await loadPosts();
  return posts.filter((p) => p.lang === lang).map(({ content, ...meta }) => meta);
}

export async function getPostBySlug(opts: { slug: string; lang: string }): Promise<BlogPost | null> {
  const posts = await loadPosts();
  return posts.find((p) => p.slug === opts.slug && p.lang === opts.lang) || null;
}

export async function getFeaturedPosts(opts: { lang: string; count?: number }): Promise<BlogPostMetadata[]> {
  const posts = await getAllPosts(opts.lang);
  return posts.slice(0, opts.count ?? 3);
}
